import { APP_SHORT_NAME } from '@/configs/app';
import slugify from 'slugify';

/**
 * Utility class for basic IndexedDB operations
 */
class IndexedDBStore<T = any> {
    private dbName: string;
    private storeName: string;
    private dbPromise: Promise<IDBDatabase>;

    constructor(dbName: string, storeName: string) {
        this.dbName = dbName;
        this.storeName = storeName;
        this.dbPromise = this.initDB();
    }

    /**
     * Initializes the IndexedDB database and object store
     */
    private initDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, 3);

            request.onupgradeneeded = (event) => {
                const db = request.result;
                const oldVersion = (event as IDBVersionChangeEvent).oldVersion;
                const possibleStores = ['posts', 'opinions'];
                // Site-native DTOs: clear legacy WP-shaped caches
                if (oldVersion < 3) {
                    possibleStores.forEach((storeName) => {
                        if (db.objectStoreNames.contains(storeName)) {
                            db.deleteObjectStore(storeName);
                        }
                    });
                }
                possibleStores.forEach((storeName) => {
                    if (!db.objectStoreNames.contains(storeName)) {
                        db.createObjectStore(storeName, { keyPath: 'id' });
                    }
                });
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Retrieves an item by its key
     * @param key - ID or key of the item
     */
    async get(key: string | number): Promise<T | null> {
        const db = await this.dbPromise;
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readonly');
            const store = tx.objectStore(this.storeName);
            const req = store.get(key);

            req.onsuccess = () => resolve(req.result ?? null);
            req.onerror = () => reject(req.error);
        });
    }

    /**
     * Stores or updates an item or an array of items
     * @param item - The item or array of items to store
     */
    async set(item: T | T[]): Promise<void> {
        const db = await this.dbPromise;
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            const store = tx.objectStore(this.storeName);

            if (Array.isArray(item)) {
                let count = 0;
                if (item.length === 0) return resolve();
                item.forEach((obj) => {
                    const req = store.put(obj);
                    req.onsuccess = () => {
                        count++;
                        if (count === item.length) resolve();
                    };
                    req.onerror = () => reject(req.error);
                });
            } else {
                const req = store.put(item);
                req.onsuccess = () => resolve();
                req.onerror = () => reject(req.error);
            }
        });
    }

    /**
     * Retrieves all items from the store
     */
    async getAll(): Promise<T[]> {
        const db = await this.dbPromise;
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readonly');
            const store = tx.objectStore(this.storeName);
            const req = store.getAll();

            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }

    /**
     * Deletes an item by key
     * @param key - ID or key of the item to delete
     */
    async delete(key: string | number): Promise<void> {
        const db = await this.dbPromise;
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            const store = tx.objectStore(this.storeName);
            const req = store.delete(key);

            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    /**
     * Deletes multiple items by keys in a single transaction
     * @param keys - Array of IDs or keys to delete
     */
    async deleteMany(keys: (string | number)[]): Promise<void> {
        if (keys.length === 0) return Promise.resolve();

        const db = await this.dbPromise;
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            const store = tx.objectStore(this.storeName);

            let completed = 0;
            let hasError = false;

            keys.forEach((key) => {
                const req = store.delete(key);
                req.onsuccess = () => {
                    completed++;
                    if (completed === keys.length && !hasError) {
                        resolve();
                    }
                };
                req.onerror = () => {
                    if (!hasError) {
                        hasError = true;
                        reject(req.error);
                    }
                };
            });
        });
    }

    /**
     * Clears all items in the store
     */
    async clear(): Promise<void> {
        const db = await this.dbPromise;
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            const store = tx.objectStore(this.storeName);
            const req = store.clear();

            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    }

    /**
     * Keeps only the most recent items based on a date field, sorted descending
     * Useful for maintaining a cache of the most recent posts
     * @param maxItems - Maximum number of items to keep
     * @param dateField - Field name containing the date (default: 'date')
     */
    async keepMostRecent(maxItems: number, dateField: string = 'date'): Promise<void> {
        // Get all items
        const allItems = await this.getAll();

        if (allItems.length <= maxItems) {
            return;
        }

        // Sort by date descending (most recent first)
        // Handle invalid dates by treating them as very old (will be deleted first)
        const sorted = allItems.sort((a: any, b: any) => {
            const dateA = a[dateField] ? new Date(a[dateField]).getTime() : 0;
            const dateB = b[dateField] ? new Date(b[dateField]).getTime() : 0;
            // If date is invalid (NaN), treat as 0 (very old)
            const validDateA = isNaN(dateA) ? 0 : dateA;
            const validDateB = isNaN(dateB) ? 0 : dateB;
            return validDateB - validDateA; // Descending order (most recent first)
        });

        // Keep only the most recent items
        const toDelete = sorted.slice(maxItems);

        // Delete older items
        if (toDelete.length > 0) {
            const deleteIds = toDelete.map((item: any) => item.id);
            await this.deleteMany(deleteIds);
        }
    }
}

/**
 * Hook to create an IndexedDB instance
 * @param dbName - The database name
 * @param storeName - The object store name
 * @returns An IndexedDBStore instance
 */
export function useIndexedDBStore<T = any>(storeName: string): IndexedDBStore<T> {
    return new IndexedDBStore<T>(
        slugify(APP_SHORT_NAME, {
            lower: true,
            trim: true,
            replacement: '-',
        }),
        storeName,
    );
}
